import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom"; // Added useLocation
import { API_BASE_URL, fetchAuthenticated } from "../utils/config";

const AppDataContext = createContext(null);

export function AppDataProvider({ children }) {
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false); // Add this flag
  const [isDeactivating, setIsDeactivating] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation(); // Track where the user is
  const hasFetched = useRef(false);

  const resetAppData = useCallback(() => {
    setUser(null);
    setCompanies(null);
    setIsSubscribed(false);
    setLoading(false);
    hasFetched.current = false;
  }, []);

  const handleLogout = useCallback(async () => {
    // 1. Set the flag immediately to block background fetches
    setIsLoggingOut(true); 

    try {
      await fetchAuthenticated(`${API_BASE_URL}/auth/signout`, { method: "POST" });
    } catch (err) {
      console.error("Signout request failed", err);
    } finally {
      resetAppData();
      navigate("/login");
      
      // Optional: Reset the flag after a delay to allow future logins
      setTimeout(() => setIsLoggingOut(false), 1000);
    }
  }, [navigate, resetAppData]);

  // --- REUSABLE LOAD DATA FUNCTION ---
  const loadData = useCallback(async () => {
    setIsLoggingOut(false);
    // If we already have a user, don't fetch again
    if (user || isDeactivating || isLoggingOut) return;
    
    try {
      setLoading(true);
      hasFetched.current = true;

      const [userRes, companiesRes, subRes] = await Promise.all([
        fetchAuthenticated(`${API_BASE_URL}/userProfile/getUserInfo`),
        fetchAuthenticated(`${API_BASE_URL}/companyProfile/getProfileInfo`),
        fetchAuthenticated(`${API_BASE_URL}/userProfile/isUserSubscribed`),
      ]);

      // 401 Check: If we are on a private route but unauthorized, log out.
      if (userRes.status === 401 || companiesRes.status === 401) {
        // Only force logout if we aren't already on a public page
        const publicPaths = ["/", "/login", "/signup",];
        if (!publicPaths.includes(window.location.pathname)) {
            handleLogout();
        }
        return;
      }

      if (!userRes.ok || !companiesRes.ok || !subRes.ok) {
        throw new Error("Failed to fetch application data");
      }

      const userJson = await userRes.json();
      const companiesJson = await companiesRes.json();
      const subJson = await subRes.json();

      const flattenedCompanies = Object.entries(companiesJson.data || {})
        .flatMap(([groupName, accounts]) =>
          (accounts || []).map(acc => ({ ...acc, groupName }))
        );

      setUser(userJson.user);
      setCompanies(flattenedCompanies);
      setIsSubscribed(subJson.isSubscribed === true);
      
    } catch (err) {
      console.error("Critical error loading app data:", err);
      hasFetched.current = false; 
    } finally {
      setLoading(false);
    }
  }, [user, handleLogout, isDeactivating, isLoggingOut]);

  useEffect(() => {
  const publicPaths = ["/", "/login", "/signup", "/forgot-password", "/reset-password", "/goodbye"];
  const isPublicPath = publicPaths.includes(location.pathname);

  // Stop if on public path, OR logging out, OR deactivating
  if (isPublicPath || isLoggingOut || isDeactivating) {
    setLoading(false);
    return;
  }

  if (!user && !hasFetched.current) {
    loadData();
    }
  }, [location.pathname, user, loadData, isLoggingOut, isDeactivating]);

  return (
    <AppDataContext.Provider
      value={{
        user,
        companies,
        setCompanies,
        isSubscribed,
        loading,
        resetAppData,
        handleLogout,
        loadData, // We export this so Login.js can call it!
        isDeactivating, 
        setIsDeactivating
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error("useAppData must be used inside AppDataProvider");
  return ctx;
}